<template>
	<button type="button" class="option-row" :disabled="disabled" @click="$emit('select')">
		<span class="option-row__body" :class="{ 'option-row__body_spaced': !amount }">
			<Text font="--text-16-20-semi" color="--text-dark-primary">{{ title }}</Text>
			<Text v-if="description" font="--text-14-20-med" :color="KOVA.textSecondary">{{ description }}</Text>
		</span>
		<span class="option-row__side">
			<span v-if="amount" class="option-row__amount">
				<Text font="--text-16-22-semi" color="--text-dark-primary" text-align="right">{{ amount }}</Text>
				<Text font="--text-14-20-med" :color="KOVA.textSecondary" text-align="right">{{ caption }}</Text>
			</span>
			<span class="option-row__arrow" aria-hidden="true">
				<img :src="arrowRight" alt="" width="17.1142" height="13.8161" />
			</span>
		</span>
	</button>
</template>

<script setup lang="ts">
// One exit on /cancel/commitment/options ("Cancel right now — $548 Today →", Figma 7415:17805; the
// paid-in-full rows, 6847:86679, have no amount — just the arrow).
// A whole-card button: the design has no separate radio + Continue, a tap goes straight on.
import { Text } from '@simple-life-app/growth-ui-kova'
import arrowRight from '@/assets/icons/arrow-right.svg'
import { KOVA } from '@/designTokens'

withDefaults(
	defineProps<{
		title: string
		description?: string
		amount?: string
		caption?: string
		disabled?: boolean
	}>(),
	{ description: undefined, amount: undefined, caption: 'Today', disabled: false },
)

defineEmits<{ (event: 'select'): void }>()
</script>

<style scoped>
.option-row {
	appearance: none;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: var(--size-16);
	width: 100%;
	padding: var(--size-20);
	background: var(--background-white);
	border: none;
	border-radius: var(--size-24);
	text-align: left;
	cursor: pointer;
	transition: filter 0.15s ease;
}

.option-row:hover:not(:disabled) {
	filter: brightness(0.98);
}

.option-row:disabled {
	cursor: default;
	opacity: 0.6;
}

.option-row:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

.option-row__body,
.option-row__side,
.option-row__amount {
	display: flex;
	flex-direction: column;
}

.option-row__body {
	flex: 1;
	min-width: 0;
	gap: var(--size-4);
}

/* The paid-in-full frame (6847:86679) spaces title and description 8 apart; the amount rows 4. */
.option-row__body_spaced {
	gap: var(--size-8);
}

.option-row__side {
	align-items: flex-end;
	gap: var(--size-12);
}

/* `button/secondary/grey`, 48×48, radius 20; icon-arrow-right 20 with the arrow inset in it. */
.option-row__arrow {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: var(--size-20);
	background: var(--background-alpha-low);
}
</style>
